import _ from 'lodash';
import { createRouter, createWebHistory } from 'vue-router';

// Login cannot be lazy loaded as it is used right away and if we don't
// load it here we get circular import issues with store and auth.
import Login from 'components/Login/Login';

import store from 'store';
import auth from 'assets/js/auth';

import { Get, urls } from 'assets/js/api';
import { navigateCtor } from './navigate';

// scrollBehavior:
// - only available in html5 history mode
// - defaults to no scroll behavior
// - return false to prevent scroll
const scrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) {
    // SavedPosition is only available for popstate navigations.
    return savedPosition;
  }

  const position = {};
  // New navigation. Scroll to anchor by returning the selector.
  if (to.hash) {
    position.selector = to.hash;
  }
  // Check if any matched route config has meta that requires scrolling to top.
  if (to.matched.some((m) => m.meta.scrollToTop)) {
    // Cords will be used if no selector is provided,
    // or if the selector didn't match any element.
    position.x = 0;
    position.y = 0;
  }
  // If the returned position is falsy or an empty object,
  // will retain current scroll position.
  return position;
};

function lazyLoad(componentName) {
  return () => import(`./../components/${componentName}.vue`);
}

export const Router = createRouter({
  history: createWebHistory(),
  base: __dirname,
  scrollBehavior,
  routes: [
    // Login paths
    {
      path: '/',
      name: 'default',
      meta: { fullscreen: true, auth: false, passwordReset: false },
      component: Login,
    },
    {
      path: '/login',
      name: 'login',
      meta: { fullscreen: true, auth: false, passwordReset: false },
      component: Login,
    },
    {
      path: '/passwordreset',
      name: 'loginpwreset',
      meta: { fullscreen: true, auth: false, passwordReset: true },
      component: Login,
    },

    // Main application
    {
      path: '/admin',
      name: 'administration',
      meta: { auth: true },
      component: lazyLoad('Admin/Admin'),
      children: [
        // Sites Administration
        {
          path: 'sites/:siteId/reports',
          name: 'sitereports',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          redirect: (to) => {
            const site = _.find(store.state.memberships.memberships,
              { site_id: to.params.siteId });
            // Not a member? Go to dashboard. (shouldn't happen)
            if (!site) return { name: 'dashboard' };
            // Go to the appropriate report depending on the plan, or the
            // dashboard if there isn't one.
            const locationName = _.get(site, 'plan.appview.appview.sidebar.' +
              'admin.site.reports.default', 'dashboard');
            if (locationName === 'dashboard') {
              console.warn('appview redirector for site reports unspecified');
            }
            return { name: `site${locationName}` };
          },
        },
        {
          path: 'sites/:siteId/reports/usage',
          name: 'siteusage',
          component: lazyLoad('Admin/Sites/Reports/SiteReportSuspenseWrapper'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/reports/capacity',
          name: 'sitecapacity',
          component: lazyLoad('Admin/Sites/Reports/SiteReportSuspenseWrapper'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/reports/heatmaps',
          name: 'siteheatmaps',
          component: lazyLoad('Admin/Sites/Reports/SiteReportSuspenseWrapper'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/reports/custom',
          name: 'sitecustomreport',
          component: lazyLoad('Admin/CustomReports/Custom'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/resourceavailabilitymaps',
          name: 'siteresourceavailabilitymaps',
          /* eslint-disable */
          component:lazyLoad('Admin/Sites/Reports/ResourceAvailabilityMaps/ResourceAvailabilityMaps'),
          /* eslint-enable */
          meta: {
            auth: true,
            adminSite: true,
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/reports/actualusage',
          name: 'siteactualusagereport',
          component:
            lazyLoad('Admin/Sites/Reports/ActualUsage/SiteActualUsage'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/reports/auditlog',
          name: 'siteauditlog',
          component: lazyLoad('Admin/AuditLog'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/reports/artwork',
          name: 'siteartwork',
          component: lazyLoad('Admin/Sites/Reports/SiteReportSuspenseWrapper'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/reports/artworkadjudicator',
          name: 'siteartworkadjudicator',
          component: lazyLoad('Admin/Sites/Reports/SiteReportSuspenseWrapper'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'reports',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/users',
          name: 'siteusers',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'users',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Users/SiteUsers'),
        },
        {
          path: 'sites/:siteId/training/types',
          name: 'sitetraining',
          component: lazyLoad('Admin/Sites/Training/SiteTraining'),
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'users',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/messaging',
          name: 'sitemessaging',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'users',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Messaging/SiteMessaging'),
        },
        {
          path: 'sites/:siteId/training',
          name: 'siteuserstraining',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'users',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Reports/Training/SiteTraining'),
        },
        {
          path: 'sites/:siteId/useraccounts',
          name: 'siteuseraccounts',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'users',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Accounts/AccountsUsers'),
        },
        {
          path: '/admin/sites/:siteId/projects',
          name: 'projectsadminsite',
          meta: {
            auth: true,
            adminSite: true,
            siteRole: 'moderator',
          },
          component: lazyLoad('Projects/Projects'),
        },
        {
          path: 'sites/:siteId/resources',
          name: 'siteresources',
          meta: {
            adminSite: true,
            auth: true,
            mainMenuRoot: 'resources',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Reservables/SiteReservables'),
        },
        {
          path: 'sites/:siteId/resources/rates',
          name: 'siteresourcesrates',
          meta: {
            adminSite: true,
            auth: true,
            mainMenuRoot: 'resources',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Reservables/SiteReservablesRates'),
        },
        {
          path: 'sites/:siteId/resources/class/:className',
          name: 'siteresourcesclass',
          meta: {
            adminSite: true,
            auth: true,
            mainMenuRoot: 'resources',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Reservables/SiteReservables'),
        },
        {
          path: 'sites/:siteId/invoices',
          name: 'site-invoices',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'invoices',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Invoices/SiteInvoices'),
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/payments',
          name: 'site-payments',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'invoices',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Invoices/Payments'),
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/subscriptions',
          name: 'site-subscriptions',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'workflows',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Subscriptions/SiteSubscriptions'),
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/triggers',
          name: 'site-triggers',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'workflows',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Subscriptions/SiteSubscriptions'),
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/invoices/:invoiceId',
          name: 'siteadmin-invoice',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'invoices',
            siteRole: 'moderator',
          },
          component: lazyLoad('Invoices/Invoice'),
          props: (route) => ({ invoiceId: route.params.invoiceId }),
        },
        {
          path: 'sites/:siteId/forms',
          name: 'siteforms',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'forms',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Forms/SiteForms'),
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/forms/:formId',
          name: 'siteeditform',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'forms',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Forms/SiteCreateEditForm'),
          props: (route) => ({
            siteId: route.params.siteId,
            formId: route.params.formId,
            editing: true,
          }),
        },
        {
          path: 'sites/:siteId/resources/scripts',
          name: 'resourcescripts',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'resources',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Scripts/SiteScripts'),
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/resources/scripts/:scriptId',
          name: 'siteeditscript',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'resources',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Scripts/SiteCreateEditScript'),
          props: (route) => ({
            siteId: route.params.siteId,
            scriptId: route.params.scriptId,
            editing: true,
          }),
        },
        {
          path: 'sites/:siteId/createform',
          name: 'sitecreateform',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'forms',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Sites/Forms/SiteCreateEditForm'),
          props: (route) => ({ siteId: route.params.siteId, editing: false }),
        },
        // Reservable editing has paths for all major attribute headings and
        // subheadings. It is organized as:
        //
        // ReservableEdit
        //  |--> Details
        //         |--> General
        //         |--> Picture
        //         |--> Visibility
        //         |--> Status
        //         |--> Related Resources
        //         \--> Attributes
        //  |--> Reservations
        //         |--> General
        //         |--> Options
        //         |--> External
        //         |--> Rates
        //         |--> Approvers
        //         |--> Capacity
        //         |--> Groups
        //         |--> Blackouts
        //         |--> Resource Blocks
        //         \--> Training
        //  |--> Maintenance
        //         \--> General
        //  \--> Files
        //         \--> List
        {
          path: 'sites/:siteId/resources',
          name: 'resourceedit',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'resources',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Edit/Reservable/ReservableEdit'),
          children: [
            // Details
            {
              path: ':reservableId/details/general',
              name: 'resourceeditdetailsgeneral',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'general',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/events',
              name: 'resourceeditdetailsevents',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'events',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/picture',
              name: 'resourceeditdetailspicture',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'picture',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/visibility',
              name: 'resourceeditdetailsvisibility',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'visibility',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/forsale',
              name: 'resourceeditdetailsforsale',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'forSale',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/status',
              name: 'resourceeditdetailsstatus',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'status',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/related',
              name: 'resourceeditdetailsrelated',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'related',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/support',
              name: 'resourceeditdetailssupport',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'support',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/barcodes',
              name: 'resourceeditdetailsbarcodes',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'barcodes',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/details/attributes',
              name: 'resourceeditdetailsattributes',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'details',
                navBarSide: 'attributes',
                siteRole: 'moderator',
              },
            },

            // Reservations
            {
              path: ':reservableId/reservations/general',
              name: 'resourceeditreservationsgeneral',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'general',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/units',
              name: 'resourceeditreservationsunits',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'units',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/scripts',
              name: 'resourceeditreservationsscripts',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'scripts',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/rates',
              name: 'resourceeditreservationsrates',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'rates',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/times',
              name: 'resourceeditreservationstimes',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'times',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/weekly-blocks',
              name: 'resourceeditreservationsweeklyblocks',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'weekly-blocks',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/weekly-slots',
              name: 'resourceeditreservationsweeklyslots',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'weekly-slots',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/approvers',
              name: 'resourceeditreservationsapprovers',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'approvers',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/groups',
              name: 'resourceeditreservationsgroups',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'groups',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/blocks',
              name: 'resourceeditreservationsblocks',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'blocks',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/training',
              name: 'resourceeditreservationstraining',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'training',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/visibility',
              name: 'resourceeditreservationsvisibility',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'visibility',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/email',
              name: 'resourceeditreservationsemail',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'email',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/external',
              name: 'resourceeditreservationsexternal',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'external',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/capacity',
              name: 'resourceeditreservationscapacity',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'capacity',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/forms',
              name: 'resourceeditreservationsforms',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'forms',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/actualusage',
              name: 'resourceeditreservationsactualusage',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'actualusage',
                siteRole: 'moderator',
              },
            },
            {
              path: ':reservableId/reservations/fields',
              name: 'resourceeditreservationsfields',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'reservations',
                navBarSide: 'fields',
                siteRole: 'moderator',
              },
            },

            // Maintenance
            {
              path: ':reservableId/maintenance/logs',
              name: 'resourceeditmaintenancelogs',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'maintenance',
                navBarSide: 'logs',
                siteRole: 'moderator',
              },
            },

            // Files
            {
              path: ':reservableId/files/general',
              name: 'resourceeditfilesgeneral',
              meta: {
                auth: true,
                adminSite: true,
                mainMenuRoot: 'resources',
                navBarTop: 'files',
                navBarSide: 'general',
                siteRole: 'moderator',
              },
            },
          ],
        },
        {
          path: 'sites/:siteId/kiosks',
          name: 'sitekiosks',
          meta: {
            auth: true,
            adminSite: true,
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Kiosks/Kiosks'),
        },
        {
          path: 'sites/:siteId/globalpayments',
          name: 'siteglobalpayments',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'invoices',
            siteRole: 'administrator',
          },
          component: lazyLoad('Admin/Integrations/GlobalPayments'),
        },
        {
          path: 'sites/:siteId/bambora',
          name: 'sitebambora',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'invoices',
            siteRole: 'administrator',
          },
          component: lazyLoad('Admin/Integrations/Bambora'),
        },
        {
          path: 'sites/:siteId/portico',
          name: 'siteportico',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'invoices',
            siteRole: 'administrator',
          },
          component: lazyLoad('Admin/Integrations/Portico'),
        },
        {
          path: 'sites/:siteId/paymentterminals',
          name: 'sitepaymentterminals',
          meta: {
            auth: true,
            adminSite: true,
            mainMenuRoot: 'invoices',
            siteRole: 'administrator',
          },
          component: lazyLoad('Admin/Integrations/PaymentTerminals'),
          props: (route) => ({ siteId: route.params.siteId }),
        },
        {
          path: 'sites/:siteId/webintegration',
          name: 'webintegration',
          meta: {
            auth: true,
            adminSite: true,
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/WebIntegration'),
        },
        {
          path: 'sites/:siteId/externalsubmissions',
          name: 'externalsubmissions',
          meta: {
            auth: true,
            adminSite: true,
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/ExternalSubmissions'),
        },
        {
          path: 'sites/:siteId/settings',
          name: 'sitesettings',
          meta: {
            adminSite: true,
            auth: true,
            mainMenuRoot: 'settings',
            siteRole: 'moderator',
          },
          props: (route) => ({ siteId: route.params.siteId }),
          component: lazyLoad('Admin/Sites/Settings/SiteSettings'),
        },

        // Portal Administration
        {
          path: 'portals/:portalId/dashboard',
          name: 'portaldashboard',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'dashboard',
          },
          component: lazyLoad('Admin/Portals/Dashboard/PortalDashboard'),
          props: (route) => ({ portalId: route.params.portalId }),
        },
        {
          path: 'portals/:portalId/reports',
          name: 'portalreports',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'reports',
          },
          redirect: (to) => {
            const portal = _.find(store.state.portals.portals,
              { portal_id: to.params.portalId });
            // Not a member? Go to dashboard. (shouldn't happen)
            if (!portal) return { name: 'dashboard' };
            // Go to the appropriate report depending on the plan, or the
            // dashboard if there isn't one.
            const locationName = _.get(portal, 'plan.appview.appview.sidebar.' +
              'admin.portal.reports.default', 'dashboard');
            if (locationName === 'dashboard') {
              console.warn('appview redirector for site reports unspecified');
            }

            return { name: `portal${locationName}` };
          },
        },
        {
          path: 'portals/:portalId/forms',
          name: 'portalforms',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'forms',
          },
          component: lazyLoad('Admin/Portals/Forms/PortalForms'),
          props: (route) => ({ portalId: route.params.portalId }),
        },
        {
          path: 'portals/:portalId/forms/:formId',
          name: 'portaleditform',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'forms',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Portals/Forms/PortalCreateEditForm'),
          props: (route) => ({
            portalId: route.params.portalId,
            formId: route.params.formId,
            editing: true,
          }),
        },
        {
          path: 'portals/:portalId/createform',
          name: 'portalcreateform',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'forms',
            siteRole: 'moderator',
          },
          component: lazyLoad('Admin/Portals/Forms/PortalCreateEditForm'),
          props: (route) => ({
            portalId: route.params.portalId,
            editing: false,
          }),
        },
        {
          path: 'portals/:portalId/webintegration',
          name: 'portalwebintegration',
          component: lazyLoad('Admin/WebIntegration'),
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
          },
        },
        {
          path: 'portals/:portalId/training',
          name: 'portaluserstraining',
          component: lazyLoad('Admin/Portals/Reports/Training/PortalTraining'),
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'users',
          },
          props: (route) => ({ portalId: route.params.portalId }),
        },
        {
          path: 'portals/:portalId/reports/custom',
          name: 'portalcustomreport',
          component: lazyLoad('Admin/CustomReports/Custom'),
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'reports',
          },
          props: (route) => ({ portalId: route.params.portalId }),
        },
        {
          path: 'portals/:portalId/reports/auditlog',
          name: 'portalauditlogreport',
          component: lazyLoad('Admin/AuditLog'),
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'reports',
          },
          props: (route) => ({ portalId: route.params.portalId }),
        },
        {
          path: 'portals/:portalId/users',
          name: 'portalusers',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'users',
          },
          props: (route) => ({ portalId: route.params.portalId }),
          component: lazyLoad('Admin/Portals/Users/PortalUsers'),
        },
        {
          path: 'portals/:portalId/useraccounts',
          name: 'portaluseraccounts',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'users',
          },
          component: lazyLoad('Admin/Portals/Accounts/AccountsUsers'),
        },
        {
          path: 'portals/:portalId/training/types',
          name: 'portaltraining',
          meta: {
            auth: true,
            adminSite: false,
            adminPortal: true,
            mainMenuRoot: 'users',
          },
          component: lazyLoad('Admin/Portals/Training/PortalTraining'),
        },
        {
          path: 'portals/:portalId/sites',
          name: 'portalsites',
          meta: { auth: true, adminSite: false, adminPortal: true },
          component: lazyLoad('Admin/Portals/Sites/PortalSites'),
        },
        {
          path: 'portals/:portalId/projects',
          name: 'portalprojects',
          meta: { auth: true, adminSite: false, adminPortal: true },
          component: lazyLoad('Admin/Portals/Projects/PortalProjects'),
        },
        {
          path: 'portals/:portalId/resources',
          name: 'portalresources',
          meta: { auth: true, adminSite: false, adminPortal: true },
          component: lazyLoad('Admin/Portals/Reservables/PortalReservables'),
        },
        {
          path: 'portals/:portalId/settings',
          name: 'portalsettings',
          meta: { auth: true, adminSite: false, adminPortal: true },
          props: (route) => ({ portalId: route.params.portalId }),
          component: lazyLoad('Admin/Portals/Settings/PortalSettings'),
        },
      ],
    },
    {
      path: '/invoices/:invoiceId',
      name: 'invoice',
      meta: { auth: true },
      component: lazyLoad('Invoices/Invoice'),
      props: (route) => ({ invoiceId: route.params.invoiceId }),
    },
    {
      path: '/project/:projectId/dashboard',
      name: 'projectDashboard',
      meta: { auth: true },
      component: lazyLoad('Projects/Dashboard/ProjectDashboard'),
    },
    {
      path: '/subscriptions',
      name: 'user-subscriptions',
      meta: {
        auth: true,
        mainMenuRoot: 'workflows',
      },
      component: lazyLoad('Subscriptions/UserSubscriptions'),
    },
    {
      path: '/help',
      name: 'user-help',
      meta: {
        auth: true,
        mainMenuRoot: 'help',
      },
      component: lazyLoad('Help/Help'),
    },
    {
      path: '/subscriptions/:subscriptionId',
      name: 'subscription',
      meta: { auth: true, mainMenuRoot: 'workflows' },
      component: lazyLoad('Subscriptions/Subscription'),
      props: (route) => ({ subscriptionId: route.params.subscriptionId }),
    },
    {
      path: '/admin/sites/:siteId/subscriptions/:subscriptionId',
      name: 'siteadmin-subscription',
      meta: {
        auth: true,
        adminSite: true,
        mainMenuRoot: 'workflows',
        siteRole: 'moderator',
      },
      component: lazyLoad('Subscriptions/Subscription'),
      props: (route) => ({
        siteId: route.params.siteId,
        subscriptionId: route.params.subscriptionId,
      }),
    },
    {
      path: '/admin/sites/:siteId/triggers/:subscriptionId',
      name: 'siteadmin-trigger',
      meta: {
        auth: true,
        adminSite: true,
        mainMenuRoot: 'workflows',
        siteRole: 'moderator',
      },
      component: lazyLoad('Subscriptions/Subscription'),
      props: (route) => ({
        siteId: route.params.siteId,
        subscriptionId: route.params.subscriptionId,
      }),
    },
    {
      path: '/projects',
      name: 'projects',
      meta: { auth: true },
      component: lazyLoad('Projects/Projects'),
    },
    {
      path: '/site/:siteId/project/:projectId/files',
      name: 'projectFiles',
      meta: { auth: true },
      component: lazyLoad('Projects/Admin/Files/ProjectFiles'),
    },
    {
      path: '/site/:siteId/project/:projectId/history',
      name: 'projectHistory',
      meta: { auth: true },
      component: lazyLoad('Projects/History/ProjectHistory'),
    },
    {
      path: '/site/:siteId/project/:projectId/users',
      name: 'projectUsers',
      meta: { auth: true },
      component: lazyLoad('Projects/Users/ProjectUsers'),
    },
    {
      path: '/createsite',
      name: 'createsite',
      meta: { auth: true },
      props: (route) => ({ parentId: route.query.parent_id }),
      component: lazyLoad('CreateSite/CreateSite'),
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      meta: { auth: true },
      component: lazyLoad('Dashboard/Dashboard'),
    },
    {
      path: '/filedownload',
      name: 'filedownload',
      meta: { auth: true },
      component: lazyLoad('Widgets/FileDownload'),
    },
    {
      path: '/history',
      name: 'history',
      meta: { auth: true },
      component: lazyLoad('History/History'),
    },
    {
      path: '/memberships',
      name: 'memberships',
      meta: { auth: true },
      component: lazyLoad('Memberships/Memberships'),
    },
    {
      path: '/reservable/notfound',
      name: 'reservable-not-found',
      meta: { auth: true },
      component: lazyLoad('Reservables/ReservableNotFound'),
    },
    {
      path: '/site/:siteId/reservable/:reservableId',
      name: 'reservable',
      meta: { auth: true },
      component: lazyLoad('Reservables/Reservable'),
    },
    {
      path: '/reservable/:reservableId',
      name: 'reservableLegacy',
      meta: { auth: true },
      component: lazyLoad('Reservables/Reservable'),
    },
    {
      path: '/site/:siteId/reservablecalendar/:reservableId',
      name: 'reservableCalendar',
      meta: { auth: true },
      component: lazyLoad('Calendar/CalendarStandalone'),
    },
    {
      path: '/reservablecalendar/:reservableId',
      name: 'reservableCalendarLegacy',
      meta: { auth: true },
      component: lazyLoad('Calendar/CalendarStandalone'),
    },
    {
      path: '/search',
      name: 'search',
      meta: { auth: true },
      component: lazyLoad('Search/Search'),
    },
    {
      path: '/searchpublic',
      name: 'searchPublic',
      meta: { fullscreen: true, auth: false },
      component: lazyLoad('Search/Search'),
    },
    {
      path: '/site/:siteId/checkindesk',
      alias: '/qrweb/site/:siteId/checkindesk',
      name: 'checkindesk',
      meta: { auth: true, siteRole: 'moderator' },
      component: lazyLoad('CheckInDesk/CheckInDesk'),
      props: (route) => ({ siteId: route.params.siteId }),
    },
    {
      path: '/site/:siteId',
      alias: '/qrweb/site/:siteId',
      name: 'site',
      meta: { auth: true },
      component: lazyLoad('Site/Site'),
    },
    {
      path: '/site/:siteId/livemaps/',
      name: 'siteLiveMaps',
      meta: { auth: true },
      component: lazyLoad('Site/SiteLiveMaps'),
      props: (route) => ({ siteId: route.params.siteId }),
    },
    {
      path: '/sitecalendar/:siteId',
      alias: '/qrweb/sitecalendar/:siteId',
      name: 'siteCalendar',
      meta: { auth: true },
      component: lazyLoad('Calendar/CalendarStandalone'),
    },
    {
      path: '/siterequests/:siteId',
      name: 'siteRequests',
      meta: { auth: true },
      component: lazyLoad('Calendar/CalendarStandalone'),
    },
    // TODO: This route needs to work without authentication, right now it
    // just redirects to login
    {
      path: '/sitepublic/:siteId',
      name: 'sitePublic',
      meta: { auth: true },
      component: { fullscreen: true, auth: false },
    },
    {
      path: '/user/:userId',
      name: 'user',
      meta: { auth: true },
      component: lazyLoad('Users/Profile/User'),
    },
    {
      path: '/usercalendar/:userId',
      name: 'userCalendar',
      meta: { auth: true },
      component: lazyLoad('Calendar/CalendarStandalone'),
    },
    {
      path: '/outlook/sites',
      alias: '/qrweb/outlook/sites',
      name: 'outlook-reservation-sites-selector',
      meta: { auth: true },
      component: lazyLoad('ReservationSiteSelector/ReservationSiteSelector'),
    },
    {
      path: '/outlook/site/:siteId',
      alias: '/qrweb/outlook/site/:siteId',
      name: 'outlook-reservation-site',
      meta: { auth: true, outlookView: true },
      component: lazyLoad('Site/Site'),
    },
    {
      path: '/chrome/sites',
      alias: '/qrweb/chrome/sites',
      name: 'chrome-reservation-sites-selector',
      meta: { auth: true, chromeExtension: true },
      component: lazyLoad('ReservationSiteSelector/ReservationSiteSelector'),
    },
    {
      path: '/chrome/site/:siteId',
      alias: '/qrweb/chrome/site/:siteId',
      name: 'chrome-reservation-site',
      meta: { auth: true, chromeExtension: true },
      component: lazyLoad('Site/Site'),
    },
  ],
});

// checkAuth checks to see if you're logged in.
const checkAuth = (to, storeArg) => {
  // No auth required.
  if (!to.matched.some((record) => record.meta.auth)) return true;

  // Auth required but not logged in.
  if (!storeArg.state.auth.auth) return false;

  // Do not allow admin accounts into the main web app.
  // Deny them access.
  if (storeArg.state.auth?.authUser.role === 'developer') return false;

  return true;
};

// checkSiteApp checks that the site's app supports this route.
const checkSiteApp = async (to, storeArg) => {
  if (!storeArg.state.memberships.loaded) {
    await storeArg.dispatch('FetchMemberships');
  }

  const site = _.find(storeArg.state.memberships.memberships,
    { site_id: to.params.siteId });

  // Not a member, so no access for you.
  if (!site) {
    return false;
  }

  return false;
};

// checkSiteRole checks to see if you have the required role in a site.
const checkSiteRole = async (to, storeArg) => {
  if (!storeArg.state.memberships.loaded) {
    await storeArg.dispatch('FetchMemberships');
  }

  let site = _.find(storeArg.state.memberships.memberships,
    { site_id: to.params.siteId });

  // Do a secondary check if we are not members or if we are users.
  // This is because you could be a user of a site but also a portal
  // administrator. This isn't too bad because regular users should
  // never even get here. If they do, there is either a bug or they
  // a using a direct URL. The "cost" is one extra API call.
  if (!site || (site.role === 'user' &&
    ['moderator', 'administrator'].indexOf(to.meta.siteRole) !== -1)) {
    // Check to see if this site is the child of any portal of this user.
    // If it is, pull down that site and inject it into our list of memberships
    // temporarily.
    let canBook = false;
    try {
      const resp = await Get(urls.SITE_CAN_EDIT(to.params.siteId));
      canBook = resp.data;
    } catch (err) {
      this.$emit('emitError',
        Error(`failed to fetch canBook for ${to.params.siteId}: ${err}`));
    }

    if (canBook) {
      await storeArg.dispatch('MembershipsReset');
      await storeArg.dispatch('FetchMemberships', to.params.siteId);
      site = _.find(storeArg.state.memberships.memberships,
        { site_id: to.params.siteId });
    }
  }

  // Not a member, will not have a valid role.
  if (!site) {
    return false;
  }

  // Moderator allows mods or administrators
  if (to.meta.siteRole === 'moderator') {
    const roles = ['moderator', 'administrator'];
    return roles.indexOf(site.role) !== -1;
  }

  // Administrators just those
  if (to.meta.siteRole === 'administrator') {
    return ['administrator'].indexOf(site.role) !== -1;
  }

  // Just in case we leak through, don't allow access.
  return false;
};

Router.beforeEach(async (to, from, next) => {
  // Clear the global error out if we just navigate away from the page.
  store.dispatch('ClearError');
  store.dispatch('ClearDeveloperStats');

  // If we are trying to grab a chrome extension page
  // mark it in session storage right away.
  if (to?.meta?.chromeExtension) {
    if (window.sessionStorage) {
      window.sessionStorage.setItem('chromeExtension', 1);
      console.debug('Marking as Chrome extension for this session.');
    }
  }

  Router.routePrev = from;
  if (to.path === '' || to.path === '/' || to.path === '/login' ||
    to.name === 'login') {
    next();
    return;
  }

  try {
    await auth.LoginWithSavedAuth({
      expiresOverride: null,
    });

    // Auth failure.
    if (!checkAuth(to, store)) {
      const queryNew = {
        redirect: to.name,
        params: encodeURI(JSON.stringify(to.params)),
      };
      _.forOwn(to.query, (value, key) => {
        queryNew[key] = value;
        // The login page uses `theme` so copy that in.
        if (key === 'theme_id') {
          queryNew['theme'] = value;
        }
      });

      next({
        name: 'login',
        query: queryNew,
      });
    } else {
      // Auth success.
      // Check if we're allowed to access this site app based on the plan.
      if (to.meta.app) {
        const validSiteApp = await checkSiteApp(to, store);
        if (!validSiteApp) {
          next({
            name: 'dashboard',
          });
          return;
        }
      }

      // Check if we're allowed to access this site's admin based on our role.
      if (to.meta.adminSite) {
        const validSiteRole = await checkSiteRole(to, store);
        if (!validSiteRole) {
          // Try offering a moderator level page to the user in case
          // they are actually a moderator in the site.
          if (to.meta.siteRole === 'administrator') {
            // This MUST be a route that requires only moderator; otherwise
            // we could end up in an infinite loop.
            next({
              name: 'siteusers',
              params: { siteId: to.params.siteId },
            });
            return;
          } else {
            next({
              name: 'dashboard',
            });
            return;
          }
        } else {
          next();
          return;
        }
      }
      next();
    }
  } catch (err) {
    if (to.meta.auth) { // Requires authentication.
      const queryNew = {
        redirect: to.name,
        params: encodeURI(JSON.stringify(to.params)),
      };
      _.forOwn(to.query, (value, key) => {
        queryNew[key] = value;
        // The login page uses `theme` so copy that in.
        if (key === 'theme_id') {
          queryNew['theme'] = value;
        }
      });

      next({
        name: 'login',
        query: queryNew,
      });
    } else { // No authentication required, you shall pass.
      next();
    }
  }
});

// Globally catch the router errors.
const originalPush = Router.push;
Router.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }
  return originalPush.call(this, location).catch((err) => {
    if (Router.isNavigationFailure(err)
      && err.name === 'NavigationDuplicated') {
      // resolve err
      return err;
    }
    // rethrow error
    return Promise.reject(err);
  });
};

const originalReplace = Router.replace;
Router.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject);
  }
  return originalReplace.call(this, location).catch((err) => {
    if (Router.isNavigationFailure(err)
      && err.name === 'NavigationDuplicated') {
      // resolve err
      return err;
    }
    // rethrow error
    return Promise.reject(err);
  });
};

Router.Navigate = navigateCtor(Router);
